// Utility functions for calculating vehicle pricing, including customer base price and type prices, fees, and caps.
const CUSTOMER_PRICE_VEHICLE_TYPES = new Set([
  'common',
  'welcab ramp',
  'welcab side seat',
  'campervan',
  'unique'
]);

export const normalizeVehicleType = (vehicleType) =>
  String(vehicleType || '').trim().toLowerCase();

const toNumber = (value) => {
  const parsedValue = Number(value || 0);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

// Rounds a number to two decimal places, returning a Number type.
const roundMoney = (value) => Number(value.toFixed(2));

export const calculateVehiclePriceBreakdown = ({
  vehicleType,
  vehiclePrice,
  customerBasePrice,
  pricingMode,
  sevsFee,
  cap,
  isCapped
}) => {
  // 'flat_per_report' pricing mode ignores vehicle type and always charges the customer base price.
  const isFlatPerReport = pricingMode === 'flat_per_report';
  const normalizedVehicleType = normalizeVehicleType(vehicleType);
  const customerPrice = isFlatPerReport || CUSTOMER_PRICE_VEHICLE_TYPES.has(normalizedVehicleType)
    ? roundMoney(toNumber(customerBasePrice))
    : 0;
  const typePrice = isFlatPerReport ? 0 : roundMoney(toNumber(vehiclePrice));
  const roundedSevsFee = roundMoney(toNumber(sevsFee));
  const uncappedBasePrice = roundMoney(customerPrice + typePrice);
  const capAmount = roundMoney(toNumber(cap));
  // If the pricing mode is not flat per report, the SEVS fee is capped, and the uncapped base price exceeds the cap, calculate the adjustment to apply.
  const adjustment = !isFlatPerReport && isCapped && uncappedBasePrice > capAmount
    ? roundMoney(uncappedBasePrice - capAmount)
    : 0;
  const cappedBasePrice = roundMoney(uncappedBasePrice - adjustment);
  const subtotalBeforeStripe = roundMoney(cappedBasePrice + roundedSevsFee);
  // Stripe's Australian domestic card rate (1.7% + $0.30) passed on to the
  // customer, charged on the capped subtotal. On Xero invoices this is folded
  // into Model Report Processing Fee (not shown as a separate line).
  const stripeFee = roundMoney(subtotalBeforeStripe * 0.017 + 0.30);
  // The price before any capping is applied, used for reporting and analytics.
  const beforeCapPrice = roundMoney(uncappedBasePrice + roundedSevsFee + stripeFee);

  return {
    customerPrice,
    typePrice,
    sevsFee: roundedSevsFee,
    stripeFee,
    beforeCapPrice,
    adjustment
  };
};